import {
  type FallRiskEntry,
  type Resident,
  type Sensor,
  type SensorKey,
  type Status,
  type UserKey,
} from "@packages/core";
import { Fragment, type FragmentProps, useEffect } from "react";
import { type Merge } from "type-fest";

import {
  useLatestFallRiskEntryQuery,
  useLatestStatusQuery,
  useResident,
  useSensor,
} from "../../services";

export type FallRiskEntrySubscriberProperties = Merge<
  FragmentProps,
  { onChange: { (fallRiskEntry: FallRiskEntry): void }; userKey?: UserKey | false | null }
>;
export function FallRiskEntrySubscriber({
  onChange,
  userKey,
  ...properties
}: FallRiskEntrySubscriberProperties) {
  const latestFallRiskEntryQuery = useLatestFallRiskEntryQuery(userKey);

  useEffect(
    function () {
      const fallRiskEntry = latestFallRiskEntryQuery.data;
      if (fallRiskEntry) {
        onChange(fallRiskEntry);
      }
    },
    [onChange, latestFallRiskEntryQuery.data],
  );

  return <Fragment {...properties} />;
}

export type ResidentSubscriberProperties = Merge<
  FragmentProps,
  { onChange: { (resident: Resident): void }; userKey: UserKey }
>;
export function ResidentSubscriber({
  onChange,
  userKey,
  ...properties
}: ResidentSubscriberProperties) {
  const resident = useResident(userKey);

  useEffect(
    function () {
      if (resident) {
        onChange(resident);
      }
    },
    [onChange, resident],
  );

  return <Fragment {...properties} />;
}

export type SensorSubscriberProperties = Merge<
  FragmentProps,
  { onChange: { (sensor: Sensor): void }; sensorKey: SensorKey }
>;
export function SensorSubscriber({
  onChange,
  sensorKey,
  ...properties
}: SensorSubscriberProperties) {
  const sensor = useSensor(sensorKey);

  useEffect(
    function () {
      if (sensor) {
        onChange(sensor);
      }
    },
    [onChange, sensor],
  );

  return <Fragment {...properties} />;
}

export type StatusSubscriberProperties = Merge<
  FragmentProps,
  { onChange: { (status: Status): void }; userKey?: UserKey | false | null }
>;
export function StatusSubscriber({ onChange, userKey, ...properties }: StatusSubscriberProperties) {
  const { data: status } = useLatestStatusQuery(userKey);

  useEffect(
    function () {
      if (status) {
        onChange(status);
      }
    },
    [onChange, status],
  );

  return <Fragment {...properties} />;
}
